import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "heroicons-react";
import { PropertyFavicon } from "./PropertyFavicon";

export type ProjectMenuItem = {
  id: string;
  name: string;
  propertyUrl?: string;
};

type Props = {
  projects: ProjectMenuItem[];
  value: string;
  onChange: (projectId: string) => void;
  menuClassName?: string;
  buttonClassName?: string;
  listClassName?: string;
};

// Native <option> elements cannot render images, so the project switch is a custom listbox.
const ProjectSelectMenu: React.FC<Props> = ({
  projects,
  value,
  onChange,
  menuClassName = "relative w-full max-w-lg sm:max-w-xs",
  buttonClassName = "mt-1 relative w-full py-2 pl-3 pr-10 border border-gray-300 bg-white rounded-md shadow-sm text-left focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm",
  listClassName = "absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg",
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selected = projects.find((project) => project.id === value);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={menuClassName}>
      <button
        type="button"
        className={buttonClassName}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <span className="flex items-center min-w-0 text-gray-900">
          <PropertyFavicon propertyUrl={selected?.propertyUrl} className="mr-2" />
          <span className="truncate">{selected?.name}</span>
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <ChevronDown className="h-5 w-5 text-gray-400" />
        </span>
      </button>
      {open && (
        <ul
          role="listbox"
          className={listClassName}
        >
          {projects.map((project) => {
            const isSelected = project.id === value;
            return (
              <li key={project.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={
                    isSelected
                      ? "w-full px-3 py-2 text-left bg-indigo-50 text-indigo-700"
                      : "w-full px-3 py-2 text-left text-gray-900 hover:bg-gray-50"
                  }
                  onClick={() => {
                    onChange(project.id);
                    setOpen(false);
                  }}
                >
                  <span className="flex items-center min-w-0">
                    <PropertyFavicon
                      propertyUrl={project.propertyUrl}
                      className="mr-2"
                    />
                    <span className="truncate">{project.name}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export { ProjectSelectMenu };
